
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { findPermission } from '@/composables/User';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'VouchersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/vouchers',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'voucher_id',
        header: '',
        fields: [{
          name: 'voucher_id',
          label: t('myShopData.vouchers.headers.id_voucher'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'voucher_info',
        header: t('myShopData.vouchers.headers.name'),
        fields: [{
          name: 'code',
          label: t('myShopData.vouchers.headers.code'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.vouchers.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.vouchers.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.vouchers.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.vouchers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.vouchers.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.vouchers.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { value: '1', label: t('active') },
              { value: '0', label: t('inactive') },
            ],
          },
        }],
      },
    ];

    /**
     * Helper function to clean strings (remove null, undefined, etc.)
     */
    const cleanStr = (value: unknown): string => {
      if (value === null || value === undefined || value === 'null' || value === 'undefined') {
        return '';
      }
      return String(value).trim();
    };

    /**
     * Handle export action
     */
    const handleExport = async () => {
      if (!findPermission('my_data.export')) {
        showToastError('PERMISSION_DENIED');
      }
      // Export logic will be handled by SpmTableAPI
    };

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      handleExport,
      cleanStr,
    };
  },
});
